<template>
  <div class="academy-events-tab" v-allowed="'edit_schedule'">
    <v-skeleton-loader v-if="loadingPublicEvents" type="card-heading, image, list-item-two-line"
      class="academy-events-tab__skeleton" />
    <template v-else>
      <section class="academy-events-tab__section">
        <h2 class="academy-events-tab__title font-roboto">
          {{ $t("message.upcoming_events") }} ({{ publicEvents.length }})
        </h2>
        <v-row v-if="publicEvents.length > 0" class="academy-events-tab__row" dense align="stretch">
          <v-col v-for="(i, index) in displayedUpcoming" :key="'public_event_' + index" cols="12" lg="3" md="6"
            class="pb-4">
            <AcademyPublicEventCard :event="i" @click="goToAcademyEvent(i._id)" />
          </v-col>
        </v-row>
        <p v-else class="academy-events-tab__empty text-body-1 font-roboto">
          {{ $t("message.no_data") }}
        </p>
        <div v-if="showUpcomingViewAll" class="academy-events-tab__view-all-wrap">
          <button type="button" class="academy-events-tab__view-all-btn font-roboto" @click="showAllUpcoming = true">
            {{ $t("message.view_all") }}
          </button>
        </div>
      </section>

      <section v-if="pastPublicEvents.length > 0" class="academy-events-tab__section academy-events-tab__section--past">
        <h2 class="academy-events-tab__title font-roboto">
          {{ $t("message.past_events") }} ({{ pastPublicEvents.length }})
        </h2>

        <v-row dense align="stretch">
          <v-col v-for="(i, index) in displayedPast" :key="'past_event_' + index" cols="12" lg="3" md="6" class="pb-4">
            <AcademyPublicEventCard :event="i" :deactivated="true" @click="goToAcademyEvent(i._id)" />
          </v-col>
        </v-row>
        <div v-if="showPastViewAll" class="academy-events-tab__view-all-wrap">
          <v-btn small outlined color="primary" class="academy-events-tab__view-all-btn font-roboto"
            @click="showAllPast = true">
            <span class="d-none d-md-inline">{{ $t("message.view_all") }}</span>
          </v-btn>
        </div>
      </section>


    </template>
  </div>
</template>

<script>
import Api from "@/services/Api2";
import AcademyPublicEventCard from "@/components/plus/calendar/AcademyPublicEventCard.vue";

const PREVIEW_COUNT = 4;

export default {
  components: {
    AcademyPublicEventCard
  },
  props: {
    activeTab: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      publicEvents: [],
      canceledPublicEvents: [],
      pastPublicEvents: [],
      loadingPublicEvents: false,
      showAllUpcoming: false,
      showAllPast: false,
      showAllCanceled: false
    };
  },
  computed: {
    displayedUpcoming() {
      if (this.showAllUpcoming || this.publicEvents.length <= PREVIEW_COUNT) {
        return this.publicEvents;
      }
      return this.publicEvents.slice(0, PREVIEW_COUNT);
    },
    displayedPast() {
      if (this.showAllPast || this.pastPublicEvents.length <= PREVIEW_COUNT) {
        return this.pastPublicEvents;
      }
      return this.pastPublicEvents.slice(0, PREVIEW_COUNT);
    },
    displayedCanceled() {
      if (this.showAllCanceled || this.canceledPublicEvents.length <= PREVIEW_COUNT) {
        return this.canceledPublicEvents;
      }
      return this.canceledPublicEvents.slice(0, PREVIEW_COUNT);
    },
    showUpcomingViewAll() {
      return this.publicEvents.length > PREVIEW_COUNT && !this.showAllUpcoming;
    },
    showPastViewAll() {
      return this.pastPublicEvents.length > PREVIEW_COUNT && !this.showAllPast;
    },
    showCanceledViewAll() {
      return this.canceledPublicEvents.length > PREVIEW_COUNT && !this.showAllCanceled;
    }
  },
  mounted() {
    this.getPublicEvents();
  },
  methods: {
    getPublicEvents() {
      this.loadingPublicEvents = true;
      Api()
        .get(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/events/public"
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            if (data.events && data.events.length > 0) this.publicEvents = data.events;
            if (data.canceledEvents && data.canceledEvents.length > 0) {
              this.canceledPublicEvents = data.canceledEvents;
            }
            if (data.pastEvents && data.pastEvents.length > 0) {
              this.pastPublicEvents = data.pastEvents;
            }
          }
          this.loadingPublicEvents = false;
          this.$emit("loaded");
        });
    },
    goToNewEvent() {
      this.navigateTo({
        name: "manageacademyeventplus",
        params: { eventId: "new" }
      });
    },
    goToAcademyEvent(id) {
      this.navigateTo({
        name: "manageacademyeventplus",
        params: { eventId: id }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.academy-events-tab {
  background: #fff;
}

.academy-events-tab__skeleton {
  background: transparent;
}

.academy-events-tab__section {
  margin-bottom: 2.5rem;
}

.academy-events-tab__section--past {
  padding-top: 0.5rem;
}

.academy-events-tab__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--v-plusGreyDarken4-base, #212121);
  letter-spacing: 0.01em;
  margin-bottom: 1.25rem;
}

.academy-events-tab__empty {
  color: var(--v-plusGreyDarken1-base, #757575);
  text-align: center;
  padding: 2rem 0;
}

.academy-events-tab__view-all-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 1rem;
  margin-top: 1.25rem;
  padding: 0.5rem 0;
}

.academy-events-tab__view-all-wrap::before,
.academy-events-tab__view-all-wrap::after {
  content: "";
  flex: 1 1 0;
  min-width: 0;
  height: 1px;
  background: #e0e0e0;
}

.academy-events-tab__view-all-btn {
  flex-shrink: 0;
  margin: 0;
  padding: 8px 20px;
  min-width: 120px;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #757575;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  cursor: pointer;
  line-height: 1.25;
  font-family: inherit;
}

.academy-events-tab__view-all-btn:hover {
  background: #f5f5f5;
}

.academy-events-tab__view-all-btn:focus {
  outline: none;
}

.academy-events-tab__view-all-btn:focus-visible {
  outline: 2px solid var(--v-plusIndigoIndigo-base, #3f51b5);
  outline-offset: 2px;
}
</style>
